import React, { useEffect, useRef, useState } from "react";
import { Plus, Paperclip, FileText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg, openPdf } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const CATALOGUE = {
  Lab: ["CBC", "ESR", "CRP", "Blood Sugar (F/PP)", "HbA1c", "Lipid Profile", "LFT", "RFT",
    "Thyroid Profile", "Urine Routine", "Culture & Sensitivity", "Biopsy / Histopathology"],
  Radiology: ["X Ray Chest", "X Ray Other", "USG Abdomen", "USG Other", "CT Scan", "MRI", "Mammography"],
  Cardiology: ["ECG", "Echocardiogram", "TMT", "Holter"],
  Procedure: ["Upper GI Endoscopy", "Colonoscopy", "Bronchoscopy", "Aspiration / Tapping"],
};
const STATUSES = ["Ordered", "Collected", "Completed", "Reports Uploaded"];
const STATUS_VARIANT = { Ordered: "outline", Collected: "default", Completed: "secondary", "Reports Uploaded": "secondary" };

export default function Investigations() {
  const { fq, user } = useApp();
  const [rows, setRows] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", category: "Lab", name: "", notes: "" });
  const [uploadTarget, setUploadTarget] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get(`/investigations${fq ? `?${fq}` : ""}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fq]);
  useEffect(() => { api.get("/patients").then(({ data }) => setPatients(data)); }, []);

  const save = async () => {
    if (!form.patient_id || !form.name) return toast.error("Select a patient and a test");
    try {
      await api.post("/investigations", { ...form, patient_id: Number(form.patient_id) });
      toast.success("Investigation ordered");
      setOpen(false); setForm({ patient_id: "", category: "Lab", name: "", notes: "" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/investigations/${id}/status?status=${encodeURIComponent(status)}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveResult = async (id, text) => {
    try {
      await api.put(`/investigations/${id}/result?result_summary=${encodeURIComponent(text)}`);
      toast.success("Result recorded"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity", "investigation");
    fd.append("entity_id", uploadTarget.id);
    fd.append("patient_id", uploadTarget.patient_id);
    try {
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Report attached (stored locally)"); load();
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };

  return (
    <div className="space-y-5" data-testid="investigations-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">Diagnostics</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Investigations</h1>
        </div>
        <Button className="gap-2" onClick={() => setOpen(true)} data-testid="order-investigation-button">
          <Plus className="h-4 w-4" /> Order investigation
        </Button>
      </div>

      <input type="file" ref={fileRef} onChange={upload} className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.dcm,.txt" data-testid="attachment-file-input" />

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="investigations-table">
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Patient</TableHead><TableHead>Category</TableHead>
              <TableHead>Test</TableHead><TableHead>Status</TableHead><TableHead>Result</TableHead>
              <TableHead>Reports</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-muted-foreground">
                  No investigations ordered yet.
                </TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`investigation-row-${r.id}`} className="hover:bg-muted/50">
                  <TableCell className="text-xs font-mono">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="font-medium">{r.patient_name}
                    <span className="block text-xs font-mono text-muted-foreground">{r.patient_uhid}</span>
                  </TableCell>
                  <TableCell><Badge variant="outline">{r.category}</Badge></TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>
                    <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                      <SelectTrigger className="h-8 w-[150px]" data-testid={`investigation-status-${r.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="max-w-[220px]">
                    <Input defaultValue={r.result_summary} className="h-8"
                      data-testid={`investigation-result-${r.id}`}
                      placeholder="Result summary"
                      onBlur={(e) => e.target.value !== r.result_summary && saveResult(r.id, e.target.value)} />
                  </TableCell>
                  <TableCell className="space-y-1">
                    {(r.attachments || []).length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                    {(r.attachments || []).map((a) => (
                      <Button key={a.id} size="sm" variant="ghost" className="h-6 gap-1 px-1.5 text-xs"
                        data-testid={`attachment-${a.id}`}
                        onClick={() => openPdf(`/attachments/${a.id}/file`)}>
                        <FileText className="h-3 w-3" /> {a.filename.slice(0, 18)}
                      </Button>
                    ))}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" className="gap-1.5" data-testid={`upload-report-${r.id}`}
                      onClick={() => { setUploadTarget(r); setTimeout(() => fileRef.current?.click(), 50); }}>
                      <Paperclip className="h-3.5 w-3.5" /> Attach
                    </Button>
                    {user.role === "super_admin" && (
                      <Button size="icon" variant="ghost" data-testid={`delete-investigation-${r.id}`}
                        onClick={async () => {
                          try { await api.delete(`/investigations/${r.id}`); toast.success("Deleted"); load(); }
                          catch (e) { toast.error(errMsg(e)); }
                        }}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="investigation-dialog">
          <DialogHeader><DialogTitle className="font-heading">Order investigation</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Patient</Label>
              <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                <SelectTrigger data-testid="investigation-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>
                  {patients.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name} · {p.uhid}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, name: "" })}>
                  <SelectTrigger data-testid="investigation-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(CATALOGUE).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Test</Label>
                <Select value={form.name} onValueChange={(v) => setForm({ ...form, name: v })}>
                  <SelectTrigger data-testid="investigation-name-select"><SelectValue placeholder="Select test" /></SelectTrigger>
                  <SelectContent>
                    {CATALOGUE[form.category].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Clinical notes / instructions</Label>
              <Textarea rows={3} value={form.notes} data-testid="investigation-notes-input"
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-investigation-button">Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
